import { useEffect, useState } from "react";
import { Briefcase, Check, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export type DocumentScopeDeal = { id: number; name: string };

/** What the person chose: the lead, or one specific deal. */
export type DocumentScopeChoice = {
  childId: number | null;
  /** "Remember my choice" was ticked — persist it for this user. */
  remember: boolean;
};

interface DocumentScopeModalProps {
  open: boolean;
  /** How many files this answer covers — shown so a drop of 6 reads honestly. */
  fileCount: number;
  /** First file's name, when a single file was dropped. */
  fileName?: string;
  leadName?: string;
  deals: DocumentScopeDeal[];
  /** The deal open in the drawer — preselected, since it is the likely target. */
  defaultDealId?: number | null;
  onConfirm: (choice: DocumentScopeChoice) => void;
  onCancel: () => void;
}

/**
 * Asked once per drop when the lead HAS deals: does this file belong to the
 * person (passport, IELTS score — true of every application) or to ONE deal
 * (an offer letter, a specific visa)? A lead with no deals never sees this.
 *
 * Both answers store the row against the lead exactly as before; the choice
 * only sets the deal marker (`Documents.childId`), so nothing is hidden from
 * anyone by answering either way.
 */
export default function DocumentScopeModal({
  open,
  fileCount,
  fileName,
  leadName,
  deals,
  defaultDealId,
  onConfirm,
  onCancel,
}: DocumentScopeModalProps) {
  const [dealId, setDealId] = useState<number | null>(
    defaultDealId ?? deals[0]?.id ?? null
  );
  const [remember, setRemember] = useState(false);

  // A fresh prompt starts fresh: re-open on another lead (or another deal in
  // the drawer) must not inherit the last answer's selection.
  useEffect(() => {
    if (!open) return;
    setDealId(defaultDealId ?? deals[0]?.id ?? null);
    setRemember(false);
  }, [open, defaultDealId, deals]);

  if (!open) return null;

  const subject =
    fileCount > 1 ? `these ${fileCount} files` : fileName || "this file";

  return (
    <Dialog open onOpenChange={(next) => !next && onCancel()}>
      {/* minmax(0,1fr): the grid's single column must never grow past the
          dialog — a long deal name used to widen it and spill every row out. */}
      <DialogContent className="grid-cols-[minmax(0,1fr)] sm:max-w-[520px] rounded-xl">
        <DialogHeader>
          <p className="pr-6 text-base font-semibold text-[#051912]">
            Where does this belong?
          </p>
          <Separator />
          <DialogDescription className="text-black-7 pt-2" asChild>
            <div>
              <p className="m-0 text-sm text-[#5c6560]">
                {leadName ? <strong>{leadName}</strong> : "This lead"} has{" "}
                {deals.length === 1 ? "a deal" : `${deals.length} deals`}. Save{" "}
                {subject} on the lead, or on one deal?
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-1 flex max-h-[50vh] flex-col gap-2 overflow-y-auto px-1">
          <button
            type="button"
            aria-pressed={dealId === null}
            onClick={() => setDealId(null)}
            className={cn(
              "flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition-colors cursor-pointer",
              dealId === null
                ? "border-[#218760] bg-[#eaf5f1]"
                : "border-[#e6e8e7] bg-white hover:bg-[#f7f9f8]"
            )}
          >
            <User
              className={cn(
                "mt-0.5 size-4 shrink-0",
                dealId === null ? "text-[#156548]" : "text-[#697571]"
              )}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-medium text-[#1e302a]">
                Lead document
              </span>
              <span className="block text-[11px] text-[#697571]">
                Belongs to the person — passport, score card, anything that is
                true of every deal.
              </span>
            </span>
            {dealId === null && (
              <Check className="mt-0.5 size-4 shrink-0 text-[#156548]" />
            )}
          </button>

          {deals.map((deal) => {
            const active = dealId === deal.id;
            return (
              <button
                key={deal.id}
                type="button"
                aria-pressed={active}
                onClick={() => setDealId(deal.id)}
                className={cn(
                  "flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition-colors cursor-pointer",
                  active
                    ? "border-[#218760] bg-[#eaf5f1]"
                    : "border-[#e6e8e7] bg-white hover:bg-[#f7f9f8]"
                )}
              >
                <Briefcase
                  className={cn(
                    "mt-0.5 size-4 shrink-0",
                    active ? "text-[#156548]" : "text-[#697571]"
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span
                    title={deal.name}
                    className="line-clamp-2 break-words text-[13px] font-medium text-[#1e302a]"
                  >
                    {deal.name}
                  </span>
                  <span className="block text-[11px] text-[#697571]">
                    Deal document — belongs to this deal only.
                  </span>
                </span>
                {active && (
                  <Check className="mt-0.5 size-4 shrink-0 text-[#156548]" />
                )}
              </button>
            );
          })}
        </div>

        <label className="mt-1 flex cursor-pointer items-start gap-2 text-[12px] text-[#5c6560]">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="mt-0.5 size-3.5 cursor-pointer accent-[#218760]"
          />
          <span>
            Remember my choice — stop asking and always save to{" "}
            {dealId === null ? "the lead" : "the open deal"}. Only for you; you
            can change it in Settings → Manage.
          </span>
        </label>

        <DialogFooter className="mt-4 flex justify-end gap-2">
          <Button variant="cancel" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm({ childId: dealId, remember })}
          >
            Upload here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
